import React from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import SEO, { createBreadcrumbSchema, createFAQSchema, softwareApplicationSchema } from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, CheckCircle, Zap, Video, TrendingUp, Sparkles, Play, Clock } from 'lucide-react';

const AIImageGeneratorTikTok = () => {
  const faqs = [
    {
      question: "What image sizes work best for TikTok?",
      answer: "TikTok uses a 9:16 vertical format (1080x1920px). Our AI generator creates perfectly sized images for TikTok thumbnails, story backgrounds, and in-video graphics."
    },
    {
      question: "Can I create TikTok thumbnails with AI?",
      answer: "Yes! Generate eye-catching TikTok video thumbnails that boost click-through rates. Create multiple variations to A/B test what works best with your audience."
    },
    {
      question: "How does AI help with TikTok content creation?",
      answer: "Our AI generates background images, overlay graphics, transition visuals, and aesthetic content that makes your TikToks stand out from the crowd."
    },
    {
      question: "Can I create content for TikTok ads?",
      answer: "Absolutely! Generate high-converting ad creatives optimized for TikTok's unique format. Create product showcases, lifestyle imagery, and branded content at scale."
    }
  ];

  const features = [
    {
      icon: <Video className="w-6 h-6" />,
      title: "9:16 Vertical Format",
      description: "Auto-generate images in TikTok's native vertical format for seamless integration"
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: "Trending Aesthetics",
      description: "Create content that matches TikTok's latest visual trends and viral styles"
    },
    {
      icon: <Sparkles className="w-6 h-6" />,
      title: "Scroll-Stopping Visuals",
      description: "AI-optimized images designed to capture attention in the first 3 seconds"
    },
    {
      icon: <Play className="w-6 h-6" />,
      title: "Video Backgrounds",
      description: "Generate stunning backgrounds for talking head videos and transitions"
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: "Ad Creative Generation",
      description: "Create TikTok ad visuals that convert browsers into buyers"
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: "Rapid Content Creation",
      description: "Generate weeks of TikTok content in a single session"
    }
  ];

  const useCases = [
    "TikTok influencers creating aesthetic content",
    "Brands running TikTok ad campaigns",
    "E-commerce stores showcasing products",
    "Content creators making educational videos",
    "Music promoters creating visual content",
    "Small businesses building TikTok presence",
    "UGC creators producing branded content",
    "TikTok Shop sellers creating product visuals"
  ];

  return (
    <>
      <SEO
        title="AI TikTok Image Generator | Create Viral Content & Thumbnails"
        description="Generate TikTok-optimized images with AI. Create viral thumbnails, video backgrounds, and ad creatives in 9:16 format. Perfect for creators and brands."
        keywords="tiktok image generator ai, tiktok thumbnail maker, ai tiktok content, tiktok video backgrounds, tiktok ad creative generator, tiktok marketing tool, viral tiktok images"
        url="/ai-image-generator-tiktok"
        jsonLd={[
          softwareApplicationSchema,
          createBreadcrumbSchema([
            { name: 'Home', url: '/' },
            { name: 'AI TikTok Image Generator', url: '/ai-image-generator-tiktok' }
          ]),
          createFAQSchema(faqs)
        ]}
      />
      
      <Header />
      
      <main className="min-h-screen bg-background">
        {/* Hero Section */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 via-pink-500/5 to-background" />
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <Badge className="mb-4 bg-gradient-to-r from-cyan-500/20 to-pink-500/20 text-foreground border-0">
                TikTok-Optimized AI Images
              </Badge>
              <h1 className="text-4xl md:text-6xl font-bold mb-6">
                AI TikTok Image Generator
                <span className="block bg-gradient-to-r from-cyan-400 to-pink-400 bg-clip-text text-transparent drop-shadow-sm">
                  Create Viral Content at Scale
                </span>
              </h1>
              <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
                Generate scroll-stopping TikTok content with AI. Thumbnails, backgrounds, 
                ad creatives - all in perfect 9:16 format. Go viral faster.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild size="lg" className="bg-gradient-to-r from-cyan-500 to-pink-500 hover:opacity-90 text-white">
                  <Link to="/try-generator">
                    Create TikTok Content Free <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button asChild size="lg" variant="outline">
                  <Link to="/gallery">View Examples</Link>
                </Button>
              </div>
              
              {/* Trust Badges */}
              <div className="flex flex-wrap justify-center gap-4 mt-8 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-cyan-500" />
                  <span>50,000+ Creators</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-cyan-500" />
                  <span>No Watermarks</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-cyan-500" />
                  <span>Commercial License</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">
              Built for TikTok Success
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {features.map((feature, index) => (
                <Card key={index} className="border border-transparent bg-gradient-to-br from-cyan-500/5 to-pink-500/5 hover:from-cyan-500/10 hover:to-pink-500/10 hover:border-cyan-500/20 transition-all shadow-sm hover:shadow-md">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 rounded-lg bg-gradient-to-r from-cyan-500/20 to-pink-500/20 flex items-center justify-center text-foreground mb-4 shadow-sm">
                      {feature.icon}
                    </div>
                    <h3 className="text-lg font-semibold mb-2">{feature.title}</h3>
                    <p className="text-muted-foreground">{feature.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Use Cases */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-12">
                Perfect For TikTok Creators & Brands
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                {useCases.map((useCase, index) => (
                  <div key={index} className="flex items-center gap-3 p-4 rounded-lg bg-gradient-to-r from-cyan-500/5 to-pink-500/5">
                    <CheckCircle className="w-5 h-5 text-cyan-500 flex-shrink-0" />
                    <span>{useCase}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">
              Frequently Asked Questions
            </h2>
            <div className="max-w-3xl mx-auto space-y-6">
              {faqs.map((faq, index) => (
                <Card key={index} className="border-0 bg-gradient-to-br from-cyan-500/5 to-pink-500/5">
                  <CardContent className="p-6">
                    <h3 className="font-semibold mb-2">{faq.question}</h3>
                    <p className="text-muted-foreground">{faq.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl font-bold mb-6">
                Start Creating Viral TikTok Content Today
              </h2>
              <p className="text-xl text-muted-foreground mb-8">
                Join thousands of TikTok creators using AI to scale their content. 
                No design skills required.
              </p>
              <Button asChild size="lg" className="bg-gradient-to-r from-cyan-500 to-pink-500 hover:opacity-90 text-white">
                <Link to="/try-generator">
                  Generate TikTok Content Free <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </>
  );
};

export default AIImageGeneratorTikTok;
